/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/metadata/options
 * @desc Column Definition for metadata options
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { getFieldsOptions } from 'labstep-web/components/Metadata/Form/fields';
import { ICONS } from 'labstep-web/constants/icons';
import {
  suppressKeyboardEventClearCells,
  suppressKeyboardEventDisableEnterOnEdit,
} from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import { FormShowEditSelectOptions } from 'labstep-web/core/Form/ShowEdit/SelectOptions';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { Label } from 'semantic-ui-react';
import styles from './styles.module.scss';

export const getMetadataOptionsColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      const variableTemplate =
        GridSmartTableProtocolConditionService.getMetadataVariableTemplate(
          experimentOrProtocol,
          metadataLabel,
        );

      const constant =
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.type.options,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolConditionService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolConditionService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel}`,
        isConstant: !!constant,
        experimentOrProtocol,
        metadata: variableTemplate || constant,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolConditionService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    suppressKeyboardEvent: (params: any): boolean =>
      suppressKeyboardEventDisableEnterOnEdit(params) ||
      suppressKeyboardEventClearCells(params),
    valueClearer: () => null,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      if (!metadata) {
        return null;
      }
      return JSON.stringify(metadata.options);
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      if (metadata && params.newValue == null) {
        return GridSmartTableProtocolConditionService.updateEntity(
          params,
          metadata.entityName,
          metadata.guid,
          {
            options: null,
          },
        );
      }
      let newValue = params.newValue;
      if (typeof newValue === 'string') {
        try {
          newValue = JSON.parse(newValue);
        } catch {
          return false;
        }
      }

      if (!metadata) {
        const template =
          GridSmartTableProtocolConditionService.getProtocolConditionVariableTemplate(
            experimentOrProtocol,
            Metadata.entityName,
            metadataLabel,
          );
        return GridSmartTableProtocolConditionService.createMetadata(
          params,
          experimentOrProtocol,
          parentEntityClass,
          metadataType,
          metadataLabel,
          protocolValueName,
          {
            options: {
              values: [],
              is_allow_multiple:
                template?.options?.is_allow_multiple !== undefined
                  ? template.options.is_allow_multiple
                  : true,
              is_allow_add:
                template?.options?.is_allow_add !== undefined
                  ? template.options.is_allow_add
                  : true,
            },
          },
        );
      }

      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        metadata.entityName,
        metadata.guid,
        {
          options: newValue,
        },
      );
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      return (
        <>
          {metadata?.optionsSelectedValues.length ? (
            metadata.optionsSelectedValues.map((value: string) => (
              <Label key={value} circular>
                {value}
              </Label>
            ))
          ) : (
            <DataGridPlaceholder
              children="Select"
              params={params}
              editable
            />
          )}
        </>
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolConditionService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
    cellEditorPopup: true,
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      if (!metadata) {
        // Metadata doesn't exist - need to create it
        const variableTemplate =
          GridSmartTableProtocolConditionService.getProtocolConditionVariableTemplate(
            experimentOrProtocol,
            Metadata.entityName,
            metadataLabel,
          );

        return (
          <div
            className={styles.container}
            style={{
              width: params.column.getActualWidth(),
            }}
          >
            <FormShowEditSelectOptions
              canEdit={true}
              children={null}
              initialEditingState
              onToggle={(): void => stopEditingAndFocus(params)}
              options={GridSmartTableProtocolConditionService.getOptions(
                params,
              )}
              field={
                getFieldsOptions(
                  variableTemplate,
                  true,
                  false,
                  true,
                )[0]
              }
              entity={variableTemplate}
              entityName={Metadata.entityName}
              createProps={{
                parentName: params.node.data.entityName,
                parentId: params.node.data.id,
                createBody: {
                  label: metadataLabel,
                  type: metadataType,
                  ...(protocolValueName && {
                    protocol_value_name: protocolValueName,
                  }),
                },
              }}
              onSubmit={(
                submittedValue: any,
                callback: () => void,
              ) => {
                GridSmartTableProtocolConditionService.createMetadata(
                  params,
                  experimentOrProtocol,
                  parentEntityClass,
                  metadataType,
                  metadataLabel,
                  protocolValueName,
                  {
                    options: submittedValue,
                  },
                );
                // Don't call callback as we want to close the editor
                stopEditingAndFocus(params);
              }}
            />
          </div>
        );
      }

      // Metadata exists but might be being created (temporary negative ID)
      if (String(metadata.id)[0] === '-') {
        stopEditingAndFocus(params);
        return '';
      }

      // Metadata exists - use normal edit mode
      return (
        <div
          className={styles.container}
          style={{
            width: params.column.getActualWidth(),
          }}
        >
          <FormShowEditSelectOptions
            canEdit={true}
            children={null}
            initialEditingState
            onToggle={(): void => stopEditingAndFocus(params)}
            options={GridSmartTableProtocolConditionService.getOptions(
              params,
            )}
            field={
              getFieldsOptions(
                metadata,
                true,
                metadata.is_template,
                true,
              )[0]
            }
            entity={metadata}
            entityName={Metadata.entityName}
          />
        </div>
      );
    },
  };
};
